import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Collapse,
  Divider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import AssistantIcon from '@mui/icons-material/Assistant'
import CreateIcon from '@mui/icons-material/Create';
import IconButton from '@mui/material/IconButton'
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import { LoadingButton } from '@mui/lab'
import Alert from '@mui/material/Alert'
import { SessionContext } from '../../context/SessionContext'
import {
  changeUnitTest,
  createTestCode,
  updateTestCodeInSession,
  updateTestListInSession,
  validateTestCode,
} from '../../service/testService'
import { toast } from 'react-toastify'
import { showToast } from '../commonUnit/Toast'
import { functionNameDetect } from '../../tool/functionNameDetect'
import { generateUnitTests } from '../../tool/unitTestGenerator'
import { generateTestCases } from './generateTestCases'
import '../../css/UnitTestList.scss'
import { AuthContext } from '../../context/AuthContext'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import BlockIcon from '@mui/icons-material/Block'
import { TestResultContext } from '../../context/TestResultContext'
import { ModeContext } from '../../context/ModeContext'
import { validateTests } from '../../tool/validateTests'
import { testsEnabled } from '../session/sessionSettings'
import {
  editorCaseLabel,
  isHiddenTest,
  isPublicTest,
  studentTestName,
  studentTestView,
  testsInStudentOrder,
} from '../session/testVisibility'
import { StudentTestCell } from '../session/TestCaseIO'
import { isClassStyle } from '../session/testResults'
import ClassCheckList from './ClassCheckList'
import { getSessionById } from '../../service/sessionService'
// `hideCreateButton` and `createUnitTestsRef` exist for the step-by-step creation wizard,
// where compiling the harness is the Next button's job rather than a separate control the
// instructor has to know to press. Both are optional and default to the old behaviour, so
// the four existing mounts (Task-UnitTest-Panel, UnitTest, StudentPanel, PresenterPanel)
// are unchanged.
//
// `onTestListChange` reports the list being edited every time it changes - the creation
// wizard's live student preview shows the test names as they are typed. Optional.
//
// `forceView` ('editor' | 'table') lets one instructor-side mount pick between the editor
// and the read-only table without flipping the global ModeContext, which PresenterPanel
// and CustomPromptPanel also drive. Used by the student-layout Task and Unit Test tab to
// show "what students see" next to "edit". Ignored for students; unset keeps the
// Mode-driven behaviour.
//
// `compact` renders the editor for narrow columns (the creation wizard's steps column and
// the Session Setting test dialog): one toolbar row, each case as a card with full-width
// fields and an icon delete, AI suggestions inline. Same state and handlers as the classic
// layout, which stays the default.
// A row's name as students see it: hidden tests (every test without `hidden: false`) as they
// always have been, public tests with their input and expected output underneath
// (testVisibility.js). Used by the student table and the instructor's matching table.
const StudentTestName = ({ testList, index }) => (
  <StudentTestCell view={studentTestView(testList, index)} renderName={(name) => <Typography fontSize={'+1'}>{name}</Typography>} />
)

const TestList = ({ hideCreateButton = false, createUnitTestsRef = null, forceView = null, onTestListChange = null, compact = false }) => {
  const { session } = useContext(SessionContext)
  const { currentUser } = useContext(AuthContext)
  const { testResult } = useContext(TestResultContext)

  const [testList, setTestList] = useState([])
  // Tracks the session.id we last synced testList from, for editors only - see the effect
  // below.
  const testListSyncedFor = useRef(null)
  const [parameter, setParameter] = useState('')
  const [returnValue, setReturnValue] = useState('')
  const [testName, setTestName] = useState('New Test')
  const [isGenerating, setIsGenerating] = useState(false)
  const [num, setNum] = useState(3)
  // 'Both' rather than the old 'Normal': 'Normal' matched no option, so the Type select
  // rendered blank. generateUnitTests treats both the same (edge + equivalence cases).
  const [type, setType] = useState('Both')
  // Compact layout: one generator row for public (example) cases and one for hidden cases
  // (testVisibility.js). The public row shares `type` with the classic layout's generator.
  const [publicNum, setPublicNum] = useState(2)
  const [hiddenNum, setHiddenNum] = useState(3)
  const [hiddenType, setHiddenType] = useState('Both')
  // The list as of the latest render, for async handlers that resume after edits.
  const testListRef = useRef(testList)
  testListRef.current = testList
  const [updatedTestResult, setUpdatedTestResult] = useState([])
  const { Mode } = useContext(ModeContext)

  const [alert, setAlert] = useState(false)
  const [alertContent, setAlertContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [color, setColor] = useState('primary')

  useEffect(() => {
    if (!session || !session.test_list) return
    // Editors (instructor/TA) type directly into these fields with per-field onBlur
    // auto-save (see the TextFields below) - if a background session sync (the 10s
    // SessionContext poll/websocket, see SessionContext.js) landed while a field had an
    // unsaved edit, this used to blindly overwrite it with the last-*saved* server value,
    // which reads as "my unit test edits keep getting deleted" whenever it happens to land
    // mid-edit. Only re-sync editors on a genuine session change (first load, or switching
    // to a different session), never on every poll tick for the same session - the same fix
    // already applied for this exact bug class in GuiExerciseEditor.jsx. Read-only viewers
    // below never type into these fields, so they keep syncing every poll to pick up
    // instructor-added tests live.
    if (currentUser.role < 3 && testListSyncedFor.current === session.id) return
    testListSyncedFor.current = session.id
    setTestList(session.test_list)
  }, [session, currentUser.role])

  // useEffect(() => {
  //   //console.log(testList)
  // }, [testList])

  useEffect(() => {
    setUpdatedTestResult(testResult)
  }, [testResult])

  useEffect(() => {
    if (onTestListChange) onTestListChange(testList)
  }, [testList]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let warning = 'Test '
    let ready = false
    for (let i = 0; i < testList.length; i++) {
      if (testList[i].newTest != null) {
        // The compact cards are titled "Example #N" / "Hidden #N", so name them that way.
        warning += (compact ? editorCaseLabel(testList, i) : i + 1) + ' and '
        ready = true
      }
    }
    if (!ready) {
      if (alert) {
        setColor('success')
      }
      setAlert(false)
    } else if (testList.length != 0) {
      setColor('error')
      setAlert(true)
    }
    if (compact) warning = warning.replace(/^Test /, '')
    warning = warning.substring(0, warning.length - 4) + 'may be incorrect '
    setAlertContent(warning)
    console.log(testList)
  }, [testList])

  if (!session) return null

  // A Python class exercise's tests are checks, edited in their own editor (same layout).
  if (isClassStyle(session) && currentUser.role < 3 && (forceView ? forceView === 'editor' : Mode == false)) {
    return (
      <ClassCheckList
        hideCreateButton={hideCreateButton}
        createUnitTestsRef={createUnitTestsRef}
        onTestListChange={onTestListChange}
      />
    )
  }

  const handleGenerateCode = async () => {
    setLoading(true)
    // try/finally: the compact editor is locked while `loading`, so a failed compile must
    // not leave it locked.
    try {
    if (testList.length === 0) {
      showToast('Please add at least one test case', 'warning')
    }
    const code = await createTestCode(testList, session.task,session.type.startsWith("Java")||session.type=="CPP"?session.type:"Python")
    // createTestCode only builds the harness and returns template: '' - writing that as-is
    // wiped the student code template every time tests were regenerated. Carry the saved
    // template over, read fresh from the server: the context copy can be up to one poll
    // (10s) behind an edit StarterCode just saved on blur.
    const freshSession = await getSessionById(session.id).catch(() => null)
    code.template = freshSession?.test_code?.template ?? session.test_code?.template ?? ''
    await updateTestCodeInSession(session.id, code)
    const validate = await validateTestCode(session.task, code,session.type.startsWith("Java")||session.type=="CPP"?session.type.toLowerCase():"python",session.id)
    console.log(validate)
    const result = validate.result
    const gptCode = validate.code
    console.log(gptCode)
    console.log(result)
    let idx = []
    let failTests = []
    for (let i = 0; i < testList.length; i++) {
      let newTestList = [...testList]
      if (result == null || !result[i]) {
        setAlert(true)
        setColor('error')
        failTests.push(testList[i])
        idx.push(i)
      } else {
        delete newTestList[i].newTest
      }
      setTestList(newTestList)
    }
    let newTestList = [...testList]
    if (failTests.length !== 0) {
      let newTests = await changeUnitTest(session.task, gptCode, failTests)
      for (let i = 0; i < idx.length; i++) {
        newTestList[idx[i]].newTest = newTests[i]
      }
    }

    // No longer sorted flagged-first: the harness above was compiled in this order and
    // students' results are matched to test_list rows by position, so re-ordering the saved
    // list paired each row's name (and a public row's input / expected output) with another
    // case's pass/fail until the next compile. The flagged cards are marked in place.
    setTestList(newTestList)

    // Was `testList` (the stale closure captured at the top of this function, before any of
    // the setTestList calls above) - the server never actually received the newTest
    // suggestions or the sort, only whatever local state looked like before this run of
    // "Create Unit Tests" started. Found while fixing the adjacent AI-generated-tests
    // deletion bug; same root cause (a save call reading a stale `testList` instead of the
    // value it just computed), different button ("Create Unit Tests", not "AI Generated
    // Unit Tests").
    // Both PUTs are awaited so a caller that reads the session back straight afterwards
    // (the creation wizard refetches on Next) cannot race them. They used to be
    // fire-and-forget, which also meant navigating away immediately could drop them.
    await updateTestListInSession(session.id, newTestList)
    console.log(code)
    // Generation takes tens of seconds (reference solution, run, repair calls); the
    // template may have been edited and saved meanwhile. Re-read it so this final write
    // does not put back the copy taken at the start.
    const latestSession = await getSessionById(session.id).catch(() => null)
    code.template = latestSession?.test_code?.template ?? code.template
    await updateTestCodeInSession(session.id, code)
    const allPassed = result != null && checker(result)
    if (allPassed) {
      setAlert(false)
      setColor('primary')
    }
    // Returned for callers that drive this programmatically (the creation wizard's Next
    // button) so they can pause on flagged cases instead of moving the instructor past
    // the suggestions this just produced. The onClick path ignores it.
    return { flagged: idx.length, total: testList.length, allPassed }
    } finally {
      setLoading(false)
    }
  }

  // Published during render rather than from an effect: handleGenerateCode is declared
  // below this component's `if (!session) return null` guard, so it is not in scope for a
  // hook declared above it. Writing to a ref during render is the standard "latest
  // callback" escape hatch and is idempotent, so a StrictMode double render is harmless.
  if (createUnitTestsRef) {
    createUnitTestsRef.current = {
      run: handleGenerateCode,
      testCount: testList.length,
      testList,
      loading,
      // Compact Generate in flight: its result is merged into the list when it lands, so
      // the wizard waits rather than compiling a list that is about to change.
      generating: isGenerating,
    }
  }

  let checker = (arr) => arr.every((v) => v === true)

  const checkString = (type,index,newTestList) =>{
    newTestList = newTestList|| [...testList];
    
    let value;
    if(type == 'parameter'){
      value = newTestList[index].parameter
    }
    else{
      value = newTestList[index].return
    }
    
    console.log(value)
    console.log(newTestList[index])
    let inQuote=-1
    for(let i = 0; i < value.length; i++){
      if(value[i]=='\'' || value[i]=='\"' ){
        if(inQuote !=-1){
          if(value[inQuote]!='\'' &&value[inQuote]!='\"' ){
            console.log(value.substring(0,inQuote)+'\"'+value.substring(inQuote))
            newTestList[index].newTest = type == 'parameter' ?{
              testName: newTestList[index].testName,
              parameter: value.substring(0,inQuote)+'\"'+value.substring(inQuote),
              return: newTestList[index].return,
              visable: false,
            } : {
              testName: newTestList[index].testName,
              parameter: newTestList[index].parameter,
              return: value.substring(0,inQuote)+'\"'+value.substring(inQuote),
              visable: false,
            }
            setTestList(newTestList)
            inQuote=-1
            return newTestList;
          }
          inQuote = -1
        }
        else{
          inQuote=i
        }
      }
      else if(value[i]=='[' || value[i]==']' || value[i]==',' ||value[i]==';'){
        if(inQuote!=-1){
          console.log(value[inQuote]=='\'' ||value[inQuote]=='\"' ? value.substring(0,i)+'\"'+value.substring(i) : value.substring(0,inQuote)+'\"'+value.substring(inQuote,i)+'\"'+value.substring(i))
          //have text with no quote
          newTestList[index].newTest = type == 'parameter' ?{
            testName: newTestList[index].testName,
            parameter: value[inQuote]=='\'' ||value[inQuote]=='\"' ? value.substring(0,i)+'\"'+value.substring(i) : value.substring(0,inQuote)+'\"'+value.substring(inQuote,i)+'\"'+value.substring(i),
            return: newTestList[index].return,
            visable: false,
          } : {
            testName: newTestList[index].testName,
            parameter: newTestList[index].parameter,
            return: value[inQuote]=='\'' ||value[inQuote]=='\"' ? value.substring(0,i)+'\"'+value.substring(i) : value.substring(0,inQuote)+'\"'+value.substring(inQuote,i)+'\"'+value.substring(i),
            visable: false,
          }
          setTestList(newTestList)
          inQuote=-1
          return newTestList;
        }
      }
      else if(inQuote==-1 && !((value[i] >= '0' && value[i] <= '9')||value[i]==' '||value[i]==='-')){
        inQuote = i
      }
    }
    if(inQuote!=-1){
      console.log(value[inQuote]=='\'' ||value[inQuote]=='\"' ? value+'\"' : value.substring(0,inQuote)+'\"'+value.substring(inQuote)+'\"')
      //ends with no quote
      newTestList[index].newTest = type == 'parameter' ?{
        testName: newTestList[index].testName,
        parameter: value[inQuote]=='\'' ||value[inQuote]=='\"' ? value+'\"' : value.substring(0,inQuote)+'\"'+value.substring(inQuote)+'\"',
        return: newTestList[index].return,
        visable: false,
      } : {
        testName: newTestList[index].testName,
        parameter: newTestList[index].parameter,
        return: value[inQuote]=='\'' ||value[inQuote]=='\"' ? value+'\"' : value.substring(0,inQuote)+'\"'+value.substring(inQuote)+'\"',
        visable: false,
      }
      setTestList(newTestList)
      
    }
    return newTestList;
  }
  const handleGenerateTest = async () => {
    if (num < 1 || num % 1 !== 0) {
      showToast('Please enter a positive integer.', 'warning')
      return
    }
    setIsGenerating(true)
    let generateTests = []
    const toastId = toast.info('Generating unit tests...', {
      autoClose: false,
    })
    try {
      generateTests = await generateUnitTests(session.task, num, type)
    } catch (e) {
      showToast('Unit tests generation failed', 'error')
      console.error(e)
      return
    } finally {
      toast.dismiss(toastId)
      setIsGenerating(false)
    }
    // The generator returns 0 (not an object) for an empty task, and the model can return
    // JSON without a unitTests array; spreading either used to throw.
    if (!Array.isArray(generateTests?.unitTests)) {
      showToast('The AI did not return any test cases. Check the problem description, then try again.', 'warning')
      return
    }
    let newTestList = [...testList, ...generateTests.unitTests]
    setTestList(newTestList)
    updateTestListInSession(session.id, newTestList)
    showToast('Unit tests generated successfully', 'success')
    if (!alert) {
      setColor('success')
    }
  }

  const handleDeleteTest = (index) => {
    let newTestList = [...testList]
    newTestList.splice(index, 1)
    // Missing setTestList was the actual bug behind "AI-generated tests can't be deleted
    // unless I refresh, and even then only the first delete sticks": this update never
    // touched local `testList`, only the server. A second delete (or any other test edit)
    // clicked before a refresh would then recompute its own newTestList from the SAME stale
    // `testList` that still had the "deleted" test in it, and its PUT would overwrite the
    // first delete's - the server only ever ends up reflecting whichever click's stale
    // snapshot happened to land last, which reads as "only the first one really deletes."
    setTestList(newTestList)
    updateTestListInSession(session.id, newTestList)
    if (!alert) {
      setColor('success')
    }
  }

  const handleEditTest = (index) => {
    let newTestList = [...testList]
    // The suggestion carries only name / input / output; keep the row's public/hidden flag
    // as it was (absent stays absent: hidden, testVisibility.js).
    const { hidden } = newTestList[index]
    newTestList[index] = { ...newTestList[index].newTest, ...(hidden !== undefined && { hidden }) }
    setTestList(newTestList)
    updateTestListInSession(session.id, newTestList).then(()=>console.log("done"))
  }

  const handleRemoveSuggestedTest = (index) => {
    let newTestList = [...testList]
    delete newTestList[index].newTest
    // Not re-sorted: the saved order must stay the order the harness was compiled in
    // (see handleGenerateCode).
    setTestList(newTestList)
    updateTestListInSession(session.id, newTestList)
  }

  // Compact layout: generate public and hidden cases in one go (generateTestCases.js). Each
  // row's count may be 0. Whatever is still missing afterwards is left in the count fields,
  // so pressing Generate again asks for exactly that.
  const handleGenerateBoth = async () => {
    const publicCount = Number(publicNum)
    const hiddenCount = Number(hiddenNum)
    const valid = (n) => Number.isInteger(n) && n >= 0
    if (!valid(publicCount) || !valid(hiddenCount) || publicCount + hiddenCount === 0) {
      showToast('Enter whole numbers for how many public and hidden cases (at least one in total).', 'warning')
      return
    }
    setIsGenerating(true)
    const toastId = toast.info('Generating unit tests...', { autoClose: false })
    let kinds
    try {
      // The editor is locked while generating, so the list cannot change under this.
      kinds = await generateTestCases({
        request: (count, kindType) => generateUnitTests(session.task, count, kindType),
        existingInputs: testListRef.current.map((test) => test.parameter),
        kinds: [
          { label: 'public', hidden: false, want: publicCount, type },
          { label: 'hidden', hidden: true, want: hiddenCount, type: hiddenType },
        ],
      })
    } finally {
      toast.dismiss(toastId)
      setIsGenerating(false)
    }

    const [publicKind, hiddenKind] = kinds
    const added = publicKind.cases.length + hiddenKind.cases.length
    if (!added) {
      if (kinds.some((kind) => kind.failed)) showToast('Unit tests generation failed', 'error')
      else showToast('Nothing added: every generated case repeated an input already in the list. Try another type, or Generate again.', 'warning')
      return
    }
    const newTestList = [...testListRef.current, ...publicKind.cases, ...hiddenKind.cases]
    setTestList(newTestList)
    updateTestListInSession(session.id, newTestList)
    if (!alert) {
      setColor('success')
    }

    const plural = (n, word) => `${n} ${word} case${n === 1 ? '' : 's'}`
    const addedText = `Added ${plural(publicKind.cases.length, 'public')} and ${plural(hiddenKind.cases.length, 'hidden')}`
    const missing = kinds.filter((kind) => kind.cases.length < kind.want)
    if (!missing.length) {
      showToast(`${addedText}.`, 'success')
      return
    }
    // Leave only what is missing in the count fields.
    setPublicNum(publicKind.want - publicKind.cases.length)
    setHiddenNum(hiddenKind.want - hiddenKind.cases.length)
    const why = missing
      .map((kind) =>
        `${kind.want - kind.cases.length} ${kind.label} short (${kind.failed ? 'the request failed' : 'the AI kept repeating inputs already in the list'})`
      )
      .join(', ')
    showToast(`${addedText}: ${why}. The counts now show what is missing - press Generate to add it.`, 'warning')
  }

  // Compact layout: make a case public (students see its input and expected output) or
  // hidden (students see only whether it passed).
  const setCaseHidden = (index, hidden) => {
    const newTestList = [...testList]
    newTestList[index] = { ...newTestList[index], hidden }
    setTestList(newTestList)
    updateTestListInSession(session.id, newTestList)
  }

  // Compact layout: add an empty case to fill in place.
  const handleAddBlankTest = () => {
    const newTestList = [
      ...testList,
      { testName: `Test ${testList.length + 1}`, parameter: '', return: '', visable: false },
    ]
    setTestList(newTestList)
    updateTestListInSession(session.id, newTestList)
    if (!alert) {
      setColor('success')
    }
  }

  // Compact layout: edit one field of one case.
  const updateCaseField = (index, field, value) => {
    const newTestList = [...testList]
    newTestList[index] = { ...newTestList[index], [field]: value }
    setTestList(newTestList)
    if (field !== 'testName' && !alert) {
      setColor('success')
    }
  }

  const handleAddTest = (e) => {
    const newTestList = [...testList]
    newTestList.push({
      testName: testName,
      parameter: parameter,
      return: returnValue,
      visable: false,
    })
    setTestList(newTestList)
    updateTestListInSession(session.id, newTestList)
    setTestName('New Test')
    setParameter('')
    setReturnValue('')
    if (!alert) {
      setColor('success')
    }
  }

  function PassIcon({ testResult }) {
    if (testResult == null) {
      return <BlockIcon sx={{ color: '#808080' }} />
    }
    if (testResult) {
      return <DoneIcon sx={{ color: '#4caf50' }} />
    } else {
      return <CloseIcon sx={{ color: '#f44336' }} />
    }
  }

  const isEditorRole = currentUser.role < 3
  const showEditor = isEditorRole && (forceView ? forceView === 'editor' : Mode == false)
  // With test cases turned off, nothing test-related is shown to students (and the
  // instructor's "what students see" table matches that). The editor stays reachable so an
  // instructor can still prepare tests before switching them back on.
  const showsTests = testsEnabled(session)
  // Generating or compiling: both finish by saving the list, so hold edits until then.
  const editorBusy = isGenerating || loading
  const showInstructorTable =
    showsTests && isEditorRole && (forceView ? forceView === 'table' : Mode == true)

  return (
    <>
      {alert && (
        <Alert severity="error" onClose={() => setAlert(false)}>
          {' '}
          {alertContent}{' '}
        </Alert>
      )}
      {showEditor && compact && (
        <div className="tl-compact" aria-busy={editorBusy}>
          {/* One row per kind - how many, what type - and one Generate for both. */}
          <div className="tl-compact__gen">
            {[
              { kind: 'Public', count: publicNum, setCount: setPublicNum, kindType: type, setKindType: setType },
              { kind: 'Hidden', count: hiddenNum, setCount: setHiddenNum, kindType: hiddenType, setKindType: setHiddenType },
            ].map((row) => (
              <div className="tl-compact__toolbar" key={row.kind}>
                <span className="tl-compact__kind" id={`tl-compact-kind-${row.kind}`}>{row.kind}</span>
                <TextField
                  className="tl-compact__count"
                  size="small"
                  label="How many"
                  type="number"
                  value={row.count}
                  disabled={editorBusy}
                  inputProps={{ min: 0, 'aria-label': `How many ${row.kind.toLowerCase()} test cases` }}
                  onChange={(e) => row.setCount(e.target.value)}
                />
                <FormControl size="small" className="tl-compact__type">
                  <InputLabel id={`tl-compact-type-${row.kind}`}>Type</InputLabel>
                  <Select
                    labelId={`tl-compact-type-${row.kind}`}
                    label="Type"
                    disabled={editorBusy}
                    // Read as "Public Type" / "Hidden Type", not two identical "Type"s.
                    SelectDisplayProps={{ 'aria-labelledby': `tl-compact-kind-${row.kind} tl-compact-type-${row.kind}` }}
                    value={row.kindType}
                    onChange={(e) => row.setKindType(e.target.value)}>
                    <MenuItem value={'Edge'}>Edge case</MenuItem>
                    <MenuItem value={'Equivalence'}>Equivalence case</MenuItem>
                    <MenuItem value={'Both'}>Both</MenuItem>
                  </Select>
                </FormControl>
                {row.kind === 'Hidden' && (
                  <LoadingButton
                    className="tl-compact__generate"
                    variant="outlined"
                    loadingPosition="start"
                    startIcon={<AutoAwesomeIcon />}
                    loading={isGenerating}
                    disabled={loading}
                    onClick={handleGenerateBoth}>
                    Generate
                  </LoadingButton>
                )}
              </div>
            ))}
            <div className="tl-compact__hint">
              Public cases are shown to students as Example #1, #2, ... with their input and
              expected output, listed first. Hidden cases show only their name and whether they
              passed.
            </div>
          </div>

          {testList.length === 0 && (
            <div className="tl-compact__empty">
              No test cases yet. Generate some with AI, or add one by hand.
            </div>
          )}

          {/* Public cases first, as students see them; `index` is still the row's place in
              test_list, which every handler uses. */}
          {testsInStudentOrder(testList).map(({ test, index }) => (
            <div
              key={index}
              className={`tl-case${test.newTest != null ? ' tl-case--flagged' : ''}`}>
              <div className="tl-case__head">
                <span className="tl-case__title">{editorCaseLabel(testList, index)}</span>
                <ToggleButtonGroup
                  className="tl-case__visibility"
                  size="small"
                  exclusive
                  value={isHiddenTest(test) ? 'hidden' : 'public'}
                  disabled={editorBusy}
                  onChange={(_e, next) => next && setCaseHidden(index, next === 'hidden')}
                  aria-label={`${editorCaseLabel(testList, index)}: public or hidden`}>
                  <ToggleButton value="public">Public</ToggleButton>
                  <ToggleButton value="hidden">Hidden</ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title="Delete this test case">
                  <IconButton
                    size="small"
                    aria-label={`Delete ${editorCaseLabel(testList, index)}`}
                    disabled={editorBusy}
                    onClick={() => handleDeleteTest(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              {/* Students see a public case only as "Example #N", so only hidden cases have a
                  name to edit. */}
              {isHiddenTest(test) && (
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  maxRows={4}
                  label="Name"
                  value={test.testName ?? ''}
                  disabled={editorBusy}
                  onChange={(e) => updateCaseField(index, 'testName', e.target.value)}
                  onBlur={() => updateTestListInSession(session.id, testList)}
                />
              )}
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={4}
                label="Input"
                value={test.parameter ?? ''}
                disabled={editorBusy}
                onChange={(e) => updateCaseField(index, 'parameter', e.target.value)}
                onBlur={() => {
                  checkString('parameter', index)
                  updateTestListInSession(session.id, testList)
                }}
              />
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={4}
                label="Expected output"
                value={test.return ?? ''}
                disabled={editorBusy}
                onChange={(e) => updateCaseField(index, 'return', e.target.value)}
                onBlur={() => {
                  checkString('return', index)
                  updateTestListInSession(session.id, testList)
                }}
              />
              {test.newTest != null && (
                <div className="tl-case__suggestion">
                  <div>
                    <strong>Suggested fix</strong> from the AI reference solution:
                    <div className="tl-case__suggestion-values">
                      Input: <code>{String(test.newTest.parameter ?? '')}</code>
                      <br />
                      Expected output: <code>{String(test.newTest.return ?? '')}</code>
                    </div>
                  </div>
                  <div className="tl-case__suggestion-actions">
                    <Button size="small" variant="contained" disabled={editorBusy} onClick={() => handleEditTest(index)}>
                      Use suggestion
                    </Button>
                    <Button size="small" color="inherit" disabled={editorBusy} onClick={() => handleRemoveSuggestedTest(index)}>
                      Keep mine
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}

          <Button className="tl-compact__add" startIcon={<AddIcon />} disabled={editorBusy} onClick={handleAddBlankTest}>
            Add test case
          </Button>

          {!hideCreateButton && (
            <LoadingButton
              variant="outlined"
              color={color}
              loadingPosition="start"
              startIcon={<CreateIcon />}
              loading={loading}
              disabled={isGenerating}
              onClick={handleGenerateCode}>
              Create Unit Tests
            </LoadingButton>
          )}
        </div>
      )}
      {showEditor && !compact && (
        <div className="scafold">
          <Typography variant="h6" fontWeight={'light'} gutterBottom>
            Test Cases
          </Typography>
          <div className="test-bar">
            {/* <Typography variant="body1" fontWeight={'light'} gutterBottom>
              Setting
            </Typography> */}
            <LoadingButton
              loadingPosition="start"
              className="btn"
              variant="outlined"
              onClick={handleGenerateTest}
              startIcon={<AssistantIcon />}
              loading={isGenerating}>
              <Typography variant="button">AI Generated Unit Tests</Typography>
            </LoadingButton>
            <FormControl variant="outlined" className="form-control">
              <TextField
                className="num-input"
                // id="outlined-number"
                label="Number"
                type="number"
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                value={num}
                onChange={(e) => setNum(e.target.value)}
              />
            </FormControl>

            <FormControl variant="outlined" className="form-control">
              <InputLabel id="type-label">Type</InputLabel>
              <Select
                className="type-select"
                labelId="type-label"
                value={type}
                onChange={(e) => setType(e.target.value)}
                label="Type">
                <MenuItem value={'Edge'}>Edge case</MenuItem>
                <MenuItem value={'Equivalence'}>Equivalence case</MenuItem>
                <MenuItem value={'Both'}>Both</MenuItem>
              </Select>
            </FormControl>
            {!hideCreateButton && (
              <LoadingButton
                loadingPosition="start"
                className="btn"
                variant="outlined"
                color={color}
                onClick={handleGenerateCode}
                startIcon={<CreateIcon />}
                loading={loading} >
                <Typography variant="button">Create Unit Tests</Typography>
              </LoadingButton>
            )}
          </div>
          <div className="add-div" style={{marginTop:".5em", paddingLeft: "0.5em"}}>
            <>
              {testsInStudentOrder(testList).map(({ test, index }) => (
                <>
                  <div style={{display: 'flex', justifyContent:"space-between",marginBottom:".2em"}}>
                    
                    <div>
                    <Typography fontSize={"+2"} fontWeight={"bold"} sx={{marginRight:".5em",marginBottom: ".5em", marginTop: ".5em",paddingTop:".3em"}}>
                          Test {index}
                          {isPublicTest(test) && (
                            <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 500, color: '#6b7280' }}>
                              (public: students see it as {studentTestName(testList, index)}, with its input and expected output)
                            </span>
                          )}
                        </Typography>
                      <div style={{display: 'flex',}}>
                      <Typography fontSize={"+2"} sx={{marginRight:"1.5em", paddingTop:".3em"}}>
                        Name: 
                        </Typography>
                        <TextField
                          className="textField"
                          variant="standard"
                          value={test.testName}
                          size="medium"
                          inputProps={{ style: { fontSize: "14px" } }}
                          onChange={(e) => {
                            const newTestList = [...testList];
                            newTestList[index].testName = e.target.value
                            setTestList(newTestList)
                          }}
                          onBlur={(e) => {
                            updateTestListInSession(session.id, testList);
                          }}
                          multiline  
                          maxRows={4} 
                          sx={{width:"15em", transform: "translateY(-.2em)", padding: ".5em", marginBottom: ".5em", 
                            border: "1px solid #ccc",  
                            borderColor: "#f3f3f3"  }}
                        ></TextField>
                      </div>
                      <div style={{display: 'flex',}}>
                        <Typography fontSize={"+2"} sx={{marginRight:"1.9em",paddingTop:".3em"}}>
                          Input:
                        </Typography>
                        <TextField
                          className="textField"
                          variant="standard"
                          value={test.parameter}
                          size="medium"
                          inputProps={{ style: { fontSize: "14px" } }}
                          onChange={(e) => {
                            const newTestList = [...testList];
                            newTestList[index].parameter = e.target.value
                            setTestList(newTestList)
                            if(!alert){
                              setColor("success")
                            }
                          }}
                          multiline  
                          maxRows={4} 
                          sx={{width:"15em", transform: "translateY(-.2em)",padding: ".5em", marginBottom: ".5em", 
                            border: "1px solid #ccc",  
                            borderColor: "#f3f3f3"  }}
                          onBlur={(e) => {
                            checkString('parameter',index)
                            updateTestListInSession(session.id, testList);
                          }}
                        ></TextField>
                      </div>
                      <div style={{display: 'flex'}}>
                        <Typography fontSize={"+2"} sx={{marginRight:"1.1em",paddingTop:".3em"}}>
                          Output:
                        </Typography>
                        <TextField
                          className="textField"
                          variant="standard"
                          value={test.return}
                          size="medium"
                          inputProps={{ style: { fontSize: "14px" } }}
                          onChange={(e) => {
                            const newTestList = [...testList];
                            newTestList[index].return = e.target.value
                            setTestList(newTestList)
                            if(!alert){
                              setColor("success")
                            }
                          }}
                          multiline  
                          maxRows={4} 
                          sx={{width:"15em", transform: "translateY(-.2em)",padding: ".5em", marginBottom: ".5em", 
                            border: "1px solid #ccc",  
                            borderColor: "#f3f3f3"  }}
                          onBlur={(e) => {
                            console.log("hey")
                            checkString('return',index)
                            updateTestListInSession(session.id, testList);
                          }}
                        ></TextField>
                      </div>
                    </div>
                    {testList[index].newTest!=null ? (
                    <div style={{display: 'flex',flexDirection:'column', alignContent:'baseline',justifyContent: 'center'}}>

                      <div style={{marginLeft:".7em"}}>
                        
                        <div style={{display: 'flex', marginBottom: "10px"}}>
                          <Typography fontSize={"+2"} sx={{marginRight:"1.9em",paddingTop:".3em"}}>
                            Input: {test.newTest.parameter}
                          </Typography>
                          
                        </div>
                        <div style={{display: 'flex',}}>
                          <Typography fontSize={"+2"} sx={{marginRight:"1.1em",paddingTop:".3em"}}>
                            Output: {test.newTest.return}
                          </Typography>
                          
                        </div>
                      </div>
                      </div>
                    ) : null}
                    <div style={{display: 'flex',flexDirection:'column', alignContent:'baseline',justifyContent: 'center', marginBottom: "10px"}}>
                    <Button
                      // variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={(e) => {
                        handleDeleteTest(index);
                      }}
                      sx={{alignContent:"bottom"}}
                    >
                      Delete
                    </Button>

                    {testList[index].newTest!=null ? 
                    (<div style={{ display: 'flex', flexDirection: 'column'}}>
                          <Button
                            startIcon={<ArrowCircleLeftIcon />}
                            style= {{align: "left"}}
                            size="small"
                            onClick={(e) => {
                              handleEditTest(index);
                            }}
                          >
                            Replace
                          </Button>
                          <Button
                            startIcon={<CloseIcon />}

                            style= {{align: "right"}}
                            color="error"
                            size="small"
                            onClick={(e) => {
                              handleRemoveSuggestedTest(index);
                            }}
                          >
                            Cancel
                            </Button>
                          
                        </div>) : null}
                        </div>
                    
                  </div>
                  <Divider flexItem sx={{ bgcolor: "Light gray",marginBottom:".2em" }} />
                </>
              ))}
              <div style={{display: 'flex', justifyContent:"space-between"}}>
                <div>
                <Typography fontSize={"+2"} fontWeight={"bold"} sx={{marginRight:".5em",marginBottom: ".5em", marginTop: ".5em",paddingTop:".3em"}}>
                      Test {testList.length+1}
                    </Typography>
                  <div style={{display: 'flex',}}>
                  <Typography fontSize={"+2"} sx={{marginRight:"1.5em",paddingTop:".3em"}}>
                      Name: 
                    </Typography>
                    <TextField
                      className="textField"
                      variant="standard"
                      value={testName}
                      size="medium"
                      inputProps={{ style: { fontSize: "14px" } }}
                      onChange={(e) => {
                        setTestName(e.target.value);
                      }}
                      multiline  
                          maxRows={4} 
                          sx={{width:"15em", transform: "translateY(-.2em)",padding: ".5em", marginBottom: ".5em", 
                            border: "1px solid #ccc",  
                            borderColor: "#f3f3f3"  }}
                    ></TextField>
                  </div>
                  <div style={{display: 'flex',}}>
                    <Typography fontSize={"+2"} sx={{marginRight:"1.9em",paddingTop:".3em"}}>
                      Input:
                    </Typography>
                    <TextField
                      className="textField"
                      variant="standard"
                      size="medium"
                      inputProps={{ style: { fontSize: "14px" } }}
                      value={parameter}
                      onChange={(e) => {
                        setParameter(e.target.value);
                      }}
                      multiline  
                          maxRows={4} 
                          sx={{width:"15em", transform: "translateY(-.2em)",padding: ".5em", marginBottom: ".5em" , 
                            border: "1px solid #ccc",  
                            borderColor: "#f3f3f3"  }}
                    ></TextField>
                  </div>
                  <div style={{display: 'flex',}}>
                    <Typography fontSize={"+2"} sx={{marginRight:"1.1em",paddingTop:".3em"}}>
                      Output:
                    </Typography>
                    <TextField
                      className="textField"
                      variant="standard"
                      size="medium"
                      inputProps={{ style: { fontSize: "14px" } }}
                      value={returnValue}
                      onChange={(e) => {
                        setReturnValue(e.target.value);
                      }}
                      multiline  
                      maxRows={4} 
                      sx={{ 
                        width: "15em", 
                        transform: "translateY(-.2em)", 
                        padding: ".5em", 
                        marginBottom: ".5em", 
                        border: "1px solid #ccc",  
                        borderColor: "#f3f3f3"      
                      }}
                    ></TextField>
                  </div>
                </div>
                <Button
                  // variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    handleAddTest();
                  }}
                ></Button>
                
              </div>
              
            </>
          </div>
          {/* <div className="test-bar">
            <LoadingButton
              loadingPosition="start"
              variant="outlined"
              className="btn"
              color={color}
              onClick={handleGenerateCode}
              startIcon={<CreateIcon />}
              loading={loading}>
              <Typography variant="button">Create Unit Tests</Typography>
            </LoadingButton>
          </div> */}
        </div>
      )}
      {/* Was `role == 3 || role == 4 || (role < 3 && Mode == true && (...))`, which for a
          student evaluates to `true` and renders nothing - i.e. this branch has only ever been
          the instructor's table view. Same behaviour, spelled out. */}
      {showInstructorTable && (
          <div className="scafold">
            <Typography variant="h6" fontWeight={'light'} gutterBottom>
              Test Cases
            </Typography>
            <div className="add-div">
              <>
                <TableContainer>
                  <Table className="testTable">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography fontSize={'+2'} fontWeight={'bold'}>
                            Name
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={'+2'} fontWeight={'bold'}>
                            Result
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {testsInStudentOrder(testList).map(({ test, index }) => (
                        <TableRow key={index}>
                          <TableCell>
                            <StudentTestName testList={testList} index={index} />
                          </TableCell>
                          <TableCell>
                            <PassIcon testResult={updatedTestResult[index]} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            </div>
          </div>
        )}
      {showsTests && (currentUser.role == 3 || currentUser.role == 4) && (
        <div className="scafold">
          <Typography variant="h6" fontWeight={'light'} gutterBottom>
            Test Cases
          </Typography>
          <div className="add-div">
            <>
              <TableContainer>
                <Table className="testTable">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography fontSize={'+2'} fontWeight={'bold'}>
                          Name
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={'+2'} fontWeight={'bold'}>
                          Result
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testsInStudentOrder(testList).map(({ test, index }) => (
                      <TableRow key={index}>
                        <TableCell>
                          <StudentTestName testList={testList} index={index} />
                        </TableCell>
                        <TableCell>
                          <PassIcon testResult={updatedTestResult[index]} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          </div>
        </div>
      )}
    </>
  )
}

export default TestList
